import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import logo from './assets/logo.svg';
import monarkaLogo from './assets/hidden_gem.png';
import { useAuth } from './hooks/useAuth';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

// Only doctors and admins are allowed into the clinic dashboard.
const ALLOWED_ROLES = ['admin', 'doctor'];

const schema = z.object({
  email: z.string().trim().min(1, 'Introduce tu email o usuario'),
  password: z.string().min(1, 'Introduce tu contraseña'),
});

type LoginValues = z.infer<typeof schema>;

/** Co-branding: typing a Monarka address pairs the two marks. */
const MonarkaConnector = () => (
  <div className="flex flex-col items-center gap-1">
    <div
      className="brand-shimmer h-0.5 w-10 rounded-full"
      style={{
        background:
          'linear-gradient(90deg, transparent, color-mix(in srgb, var(--primary) 60%, transparent), rgba(219,112,147,0.6), transparent)',
        backgroundSize: '200% 100%',
      }}
    />
    {/* Monarka's pink, not ours: it belongs to their brand, so it stays a
     *  literal rather than becoming one of our tokens. */}
    <div className="brand-heartbeat text-base text-[#db7093]">♥</div>
    <div
      className="brand-shimmer h-0.5 w-10 rounded-full"
      style={{
        background:
          'linear-gradient(90deg, transparent, rgba(219,112,147,0.6), color-mix(in srgb, var(--primary) 60%, transparent), transparent)',
        backgroundSize: '200% 100%',
      }}
    />
  </div>
);

const Login = () => {
  const [authError, setAuthError] = useState('');
  const { login, logout } = useAuth();
  const navigate = useNavigate();

  const form = useForm<LoginValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const email = form.watch('email');
  const isMonarka = email.trim().toLowerCase().endsWith('@monarkaclinic.com');
  const isSubmitting = form.formState.isSubmitting;

  const onSubmit = async (values: LoginValues) => {
    setAuthError('');
    try {
      const user = await login(values.email, values.password);
      if (!ALLOWED_ROLES.includes(user.role)) {
        logout();
        setAuthError('No tienes permiso para acceder.');
        return;
      }
      navigate('/app');
    } catch (err) {
      // A rejected sign-in comes back as 401 with the backend's own English
      // wording, which has no place in the form; anything else keeps its
      // message, since it carries information this screen cannot guess.
      const status = (err as { status?: number } | null)?.status;
      setAuthError(
        status === 401
          ? 'Credenciales incorrectas'
          : err instanceof Error && err.message
            ? err.message
            : 'No se pudo iniciar sesión. Revisa tus credenciales.',
      );
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-app-background px-4 py-10">
      <div className="w-full max-w-[440px] rounded-lg border border-border bg-card px-6 py-10 shadow-overlay sm:px-10">
        <div className="mb-9 flex flex-col items-center">
          <div
            className={`mb-5 flex items-center justify-center transition-all duration-300 ${
              isMonarka ? 'gap-5' : 'gap-0'
            }`}
          >
            <img
              src={logo}
              alt=""
              className={`size-15 transition-all duration-300 ${isMonarka ? 'brand-pulse' : ''}`}
            />
            {isMonarka && <MonarkaConnector />}
            {isMonarka && (
              <img
                src={monarkaLogo}
                alt=""
                className="brand-pulse h-20 w-[70px] rounded-md object-cover object-left"
              />
            )}
          </div>

          <h1 className="mb-2 font-display text-3xl font-semibold">
            {isMonarka ? 'Monarka Clinic' : 'Sissi Clinic'}
          </h1>
          <p className="text-center text-[15px] text-muted-foreground">
            Inicia sesión para continuar
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email o Usuario</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      autoComplete="username"
                      placeholder="tu@email.com"
                      disabled={isSubmitting}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contraseña</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="current-password"
                      placeholder="••••••••"
                      disabled={isSubmitting}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* A rejected sign-in belongs to the form, not to one field: the
             *  server does not say which of the two was wrong. role="alert"
             *  makes a screen reader announce it without moving focus. */}
            {authError && (
              <p
                role="alert"
                className="rounded-control border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
              >
                {authError}
              </p>
            )}

            <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="animate-spin" />}
              {isSubmitting ? 'Iniciando sesión…' : 'Iniciar sesión'}
            </Button>
          </form>
        </Form>

        <div className="mt-8 border-t border-border pt-6 text-center">
          <p className="text-[13px] text-muted-foreground">
            © 2026 Sissi Clinic. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
